import React, { useEffect, useState, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMoreProductDetails } from "../../../../../utils/tripBuilder";
import { formatPrice } from "@/lib/general";
import ImageGalleryModal from "../../../image/FullScreenGallery";
import {
  ViatorProductResponse,
  ViatorProductInitial,
  Location,
  TripUser,
} from "@/types";
import { addViatorProductToDay } from "@/lib/experiences";
import { motion } from "framer-motion";
import logger from "@/utils/logger";

import Carousel from "./Carousel";
import TourInclusions from "./Inclusions";
import LocationComponent from "./Location";
import ProductTypes from "./Types";
import Cancellation from "./CancellationPolicy";
import AdditionalInfo from "./AdditionalInfo";

import useTripStore from "@/stores/tripBuilder/useTrip";
import useAuthStore from "@/stores/user/authStore";

interface SectionContainerProps {
  children: React.ReactNode;
  className?: string;
}

const SectionContainer: React.FC<SectionContainerProps> = ({
  children,
  className,
}) => <div className={`w-full`}>{children}</div>;

interface ExperienceAttributeProps {
  icon: string;
  name: string;
  value: string | number;
  className?: string;
  currency?: string;
}

const ExperienceAttribute: React.FC<ExperienceAttributeProps> = ({
  icon,
  name,
  value,
  className,
  currency,
}) => {
  return (
    <div
      className={`flex flex-col items-start justify-start ring-1 ring-neutral-200 shadow rounded-lg p-2 w-full ${className}`}
    >
      <div className="flex items-center space-x-2.5 mb-2">
        <i className={`${icon} text-neutral-700 text-base`}></i>
        <h1 className="font-semibold text-neutral-900 text-xs">{name}</h1>
      </div>
      <p className="text-sm text-neutral-900 font-[325] pl-1">
        {name === "Price" ? (
          <>
            <span className="text-neutral-900">
              {formatPrice(currency || "USD", Number(value))}
            </span>
            <span className="text-neutral-400"> per person</span>
          </>
        ) : (
          <>
            {value}
            {name === "Rating" && (
              <span className="text-neutral-400"> out of 5</span>
            )}
            {name === "Total Reviews" && (
              <span className="text-neutral-400"> reviews</span>
            )}
          </>
        )}
      </p>
    </div>
  );
};

interface AddressComponentProps {
  address: string;
}

const AddressComponent: React.FC<AddressComponentProps> = ({ address }) => {
  return (
    <div className="flex flex-row items-center space-x-2 text-neutral-600 relative">
      <i className="fa-light fa-map-marker-alt fa-md text-[15px]"></i>
      <p className={`text-[13px] font-light`}>{address}</p>
    </div>
  );
};

const formatImages = (
  images: {
    caption: string;
    imageSource: string;
    isCover: boolean;
    variants: { url: string; width: number; height: number }[];
  }[]
) => {
  return images.map((image) => {
    // Find the highest resolution variant once and reuse
    const bestVariant = image.variants.reduce(
      (maxRes, variant) => {
        if (variant.width > maxRes.width && variant.height > maxRes.height) {
          return variant;
        }
        return maxRes;
      },
      { width: 0, height: 0, url: image.imageSource }
    );

    // Find a lower resolution variant for preview
    const lowResVariant = image.variants.reduce(
      (bestLowRes, variant) => {
        // Target around 800px width for low res
        if (
          variant.width > bestLowRes.width &&
          variant.width < 800 &&
          variant.height > bestLowRes.height
        ) {
          return variant;
        }
        return bestLowRes;
      },
      { width: 0, height: 0, url: image.imageSource }
    );

    return {
      caption: image.caption,
      lowRes: lowResVariant.url,
      highRes: bestVariant.url,
    };
  });
};

interface MoreInfoModalViatorProps {
  updateTripData: (tripData: any) => void;
  product: ViatorProductInitial | null;
  selectedLocation: Location;

  handleClose: (modalRef: any) => void;
  isAddDestinationModalOpen: boolean;
  shouldClose: boolean;
  isAdmin: boolean;
}

const MoreInfoModalViator: React.FC<MoreInfoModalViatorProps> = ({
  updateTripData,
  product,
  selectedLocation,
  handleClose,
  isAddDestinationModalOpen,
  shouldClose,
  isAdmin,
}) => {
  const { user } = useAuthStore();
  const { tripData, selectedDay } = useTripStore();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [height, setHeight] = useState(window.innerHeight - 500);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [pillHover, setPillHover] = useState(false);
  const [productImages, setProductImages] = useState<
    { caption: string; lowRes: string; highRes: string }[]
  >([]);

  useEffect(() => {
    if (shouldClose) {
      handleClose(modalRef);
    }
  }, [shouldClose, handleClose]);

  useEffect(() => {
    if (product) {
      setProductImages(formatImages(product.images));
    }
  }, [product]);

  const isLiked = useMemo(
    () => tripData?.liked_experiences.includes(product?.product_code || ""),
    [tripData?.liked_experiences, product?.product_code]
  );

  useEffect(() => {
    if (isAddDestinationModalOpen) {
      handleClose(modalRef);
    }
  }, [isAddDestinationModalOpen, handleClose]);

  const { data: productDetails, isLoading: isProductDetailsLoading } =
    useQuery<ViatorProductResponse>({
      queryKey: ["viator", product?.product_code],
      queryFn: () => {
        if (!product) {
          throw new Error("Product is null");
        }
        return getMoreProductDetails(
          product.product_code,
          selectedLocation?.viator_id
        );
      },
      enabled: !!product,
      staleTime: Infinity,
    });

  const handleAddToDay = async () => {
    if (
      !productDetails ||
      !tripData ||
      !selectedLocation ||
      !selectedDay ||
      !product ||
      !user
    )
      return;
    setIsAdding(true);
    const tripUser: TripUser = {
      user_id: user.user_id,
      first_name: user.first_name,
      last_name: user.last_name,
      username: user.username,
      profile_image: user.profile_image,
      is_admin: true,
      creator: true,
    };
    const updatedTripData = await addViatorProductToDay(
      productDetails,
      product,
      tripData,
      selectedLocation,
      selectedDay,
      tripUser
    );
    updateTripData(updatedTripData);
    // Recalculate location budget
    useTripStore.getState().recalculateLocationBudget(selectedLocation.id);
    // Create location update
    await useTripStore
      .getState()
      .createLocationUpdate(
        selectedLocation.waytora_id,
        selectedLocation.id,
        "experience_added",
        {
          itemName: productDetails.name,
          itemType: "experience",
          duration: productDetails.duration,
          images: productDetails.images.map((image: any) => image.highRes),
        }
      );
    setTimeout(() => {
      setIsAdding(false);
    }, 1000);
  };

  const handleLike = () => {
    if (!tripData || !product) return;
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
    }, 3000);
    logger.log("Handling like for:", product?.product_code);
    const newLikedState = !tripData.liked_experiences.includes(
      product?.product_code
    );
    updateTripData({
      ...tripData,
      liked_experiences: newLikedState
        ? [...tripData.liked_experiences, product?.product_code]
        : tripData.liked_experiences.filter(
            (id) => id !== product?.product_code
          ),
    });
  };

  const handleLeftClick = () => {
    const carousel = document.getElementById(
      "expPhotoCarousel"
    ) as HTMLDivElement;
    carousel.scrollBy({ left: -300, behavior: "smooth" });
    setCurrentImageIndex(currentImageIndex - 1);
  };

  const handleRightClick = () => {
    const carousel = document.getElementById(
      "expPhotoCarousel"
    ) as HTMLDivElement;
    carousel.scrollBy({ left: 300, behavior: "smooth" });
    setCurrentImageIndex(currentImageIndex + 1);
  };

  const openModal = () => setIsModalOpen(true);

  return (
    <motion.div
      className="absolute inset-0 bg-white/20 backdrop-blur-sm z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="w-full h-full p-3 flex flex-col gap-1">
        <div className="h-full bg-white/90 backdrop-blur-xl rounded-lg px-4 pb-2 flex flex-col border border-neutral-200">
          <div className="flex flex-col items-center justify-start relative">
            <div className="w-full overflow-y-scroll no-scrollbar h-[calc(100vh-3rem)] rounded-lg mt-2 pb-[5rem]">
              <div className="flex flex-row justify-between items-start pt-3">
                <div className="flex-1 max-w-[calc(100%-5rem)] sticky top-0 z-10 pb-1">
                  <div className="flex flex-col items-start justify-between">
                    <div className="pb-2">
                      <img
                        src="/assets/external-logos/viator-logo.svg"
                        alt="Viator Logo"
                        className="w-16 object-cover px-2.5 py-1.5 ring-inset ring-[1.5px] ring-[#196B6E] rounded-full"
                      />
                    </div>
                    <h2 className="text-lg font-medium text-neutral-800 pt-1 mb-0.5 line-clamp-2">
                      {product?.title}
                    </h2>
                    <AddressComponent
                      address={
                        selectedLocation?.address_components.city +
                        ", " +
                        selectedLocation?.address_components.state
                      }
                    />
                  </div>
                </div>
                <div className="flex-none flex flex-col items-center justify-start gap-2 ml-4">
                  <button
                    onClick={handleClose}
                    className="text-xs text-neutral-600 hover:text-neutral-800 border border-neutral-200 hover:border-neutral-600 h-8 rounded-full transition-all duration-300 w-16 flex items-center justify-center gap-1"
                  >
                    <i className="fa-solid fa-xmark text-[11px]"></i>
                    Close
                  </button>
                  {isAdmin && (
                    <button
                      onClick={handleAddToDay}
                      className="text-xs text-white bg-indigo-600 hover:bg-indigo-700 h-8 rounded-full transition-all duration-300 w-16 flex items-center justify-center gap-1"
                    >
                      {isAdding ? (
                        <i className="fa-solid fa-spinner fa-spin text-[11px]"></i>
                      ) : (
                        <>
                          <i className="fa-solid fa-plus text-[11px]"></i>
                          <span>Add</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
              <ProductTypes product={product} />

              {/* image carousel */}
              <div className="relative w-full flex flex-col items-center justify-start py-2">
                <button
                  onClick={handleLeftClick}
                  className="absolute top-1/2 left-4 -translate-y-1/2 w-7 h-7 rounded-full bg-white bg-opacity-70 hover:bg-opacity-100 shadow-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 z-10"
                  aria-label="Previous image"
                >
                  <i className="fas fa-sm fa-chevron-left text-gray-800"></i>
                </button>

                <div className="w-full max-w-3xl overflow-hidden rounded-lg">
                  <Carousel images={productImages} openModal={openModal} />
                </div>

                <button
                  onClick={handleRightClick}
                  className="absolute top-1/2 right-4 -translate-y-1/2 w-7 h-7 rounded-full bg-white bg-opacity-70 hover:bg-opacity-100 shadow-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 z-10"
                  aria-label="Next image"
                >
                  <i className="fas fa-sm fa-chevron-right text-gray-800"></i>
                </button>
              </div>

              {isProductDetailsLoading ? (
                <div className="space-y-[2rem] py-4 px-1">
                  {/* Description Section */}
                  <div className="w-full flex flex-col items-start justify-start">
                    <div className="h-5 w-24 bg-neutral-200 rounded-md mb-3"></div>
                    <div className="w-full space-y-2">
                      <div className="h-4 w-full bg-neutral-200 rounded-md"></div>
                      <div className="h-4 w-full bg-neutral-200 rounded-md"></div>
                      <div className="h-4 w-3/4 bg-neutral-200 rounded-md"></div>
                    </div>
                  </div>

                  {/* Attributes Grid */}
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-20 bg-neutral-200 rounded-lg p-2"
                      ></div>
                    ))}
                  </div>

                  {/* What's Included Section */}
                  <div className="w-full">
                    <div className="h-5 w-32 bg-neutral-200 rounded-md mb-4"></div>
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center space-x-3">
                          <div className="h-4 w-4 bg-neutral-200 rounded-full"></div>
                          <div className="h-4 w-full bg-neutral-200 rounded-md"></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* What's Not Included Section */}
                  <div className="w-full">
                    <div className="h-5 w-36 bg-neutral-200 rounded-md mb-4"></div>
                    <div className="space-y-3">
                      {[1, 2].map((i) => (
                        <div key={i} className="flex items-center space-x-3">
                          <div className="h-4 w-4 bg-neutral-200 rounded-full"></div>
                          <div className="h-4 w-full bg-neutral-200 rounded-md"></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Location Section */}
                  <div className="w-full">
                    <div className="h-5 w-32 bg-neutral-200 rounded-md mb-4"></div>
                    <div className="h-[400px] w-full bg-neutral-200 rounded-lg"></div>
                  </div>

                  {/* Additional Info Section */}
                  <div className="w-full">
                    <div className="h-5 w-32 bg-neutral-200 rounded-md mb-4"></div>
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-4 w-full bg-neutral-200 rounded-md"
                        ></div>
                      ))}
                    </div>
                  </div>

                  {/* Cancellation Policy Section */}
                  <div className="w-full">
                    <div className="h-5 w-40 bg-neutral-200 rounded-md mb-4"></div>
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-4 w-full bg-neutral-200 rounded-md"
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-[2rem] py-4 px-1">
                  <div className="w-full flex flex-col items-start justify-start">
                    <h3 className="text-[15px] font-semibold mb-2 text-neutral-800">
                      Description
                    </h3>
                    <p className="text-neutral-700 font-light text-[13px] leading-relaxed">
                      {productDetails?.metadata.description}
                    </p>
                  </div>
                  <div className="grid grid-flow-row auto-rows-auto grid-cols-3 items-center justify-between w-full gap-2">
                    <ExperienceAttribute
                      icon="fa-regular fa-money-bill-wave"
                      name="Price"
                      value={product?.from_price || "N/A"}
                      currency={product?.currency}
                    />
                    <ExperienceAttribute
                      icon="fa-regular fa-star"
                      name="Rating"
                      value={roundRatingToOneDecimal(
                        productDetails?.rating || 0
                      )}
                    />
                    <ExperienceAttribute
                      icon="fa-regular fa-comment-dots"
                      name="Total Reviews"
                      value={formatTotalReviews(
                        productDetails?.total_reviews || 0
                      )}
                    />
                  </div>
                  <SectionContainer>
                    <div className="w-full flex flex-col items-start justify-start">
                      <h3 className="text-[15px] font-semibold mb-4 text-neutral-800">
                        What's Included?
                      </h3>
                      <TourInclusions
                        type="inclusions"
                        inclusions={productDetails!.inclusions.filter(
                          (inclusion) => inclusion.description === null
                        )}
                      />
                    </div>
                  </SectionContainer>
                  <SectionContainer>
                    <div className="w-full flex flex-col items-start justify-start">
                      <h3 className="text-[15px] font-semibold mb-4 text-neutral-800">
                        What's Not Included?
                      </h3>
                      <TourInclusions
                        type="exclusions"
                        inclusions={productDetails!.exclusions.filter(
                          (inclusion) => inclusion.description === null
                        )}
                      />
                    </div>
                  </SectionContainer>

                  {productDetails?.location &&
                    productDetails?.location.length > 0 && (
                      <SectionContainer>
                        <div className="w-full flex flex-col items-start justify-start">
                          <h3 className="text-[15px] font-semibold mb-2 text-neutral-800">
                            Where to Find It?
                          </h3>
                          <LocationComponent
                            locations={productDetails?.location}
                            height={height}
                            locationDetails={product}
                          />
                        </div>
                      </SectionContainer>
                    )}

                  <SectionContainer>
                    <div className="w-full flex flex-col items-start justify-start">
                      <h3 className="text-[15px] font-semibold mb-4 text-neutral-800">
                        Additional Info
                      </h3>
                      <AdditionalInfo productDetails={productDetails} />
                    </div>
                  </SectionContainer>

                  <SectionContainer>
                    <div className="w-full flex flex-col items-start justify-start">
                      <h3 className="text-[15px] font-semibold mb-2 text-neutral-800">
                        Cancellation Policy
                      </h3>
                      <Cancellation
                        policy={productDetails!.cancellationPolicy}
                      />
                    </div>
                  </SectionContainer>
                </div>
              )}
            </div>
            {isModalOpen && (
              <ImageGalleryModal
                images={productImages.map((image) => image.highRes)}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                alt={product?.title || ""}
                title={product?.title || ""}
              />
            )}
            {/* <div
              onMouseEnter={() => setPillHover(true)}
              onMouseLeave={() => setPillHover(false)}
              className={`absolute bottom-5 left-1/2 transform -translate-x-1/2 bg-white rounded-full ring-1 shadow-lg z-10 overflow-hidden ${
                pillHover ? "w-48 " : "w-44 "
              } transition-all duration-500 ease-in-out ${
                pillHover
                  ? "opacity-100 ring-neutral-300"
                  : "opacity-95  ring-neutral-200"
              }`}
            >
              <div
                className={`w-full flex flex-row items-center justify-between ${
                  pillHover ? "px-8 h-[3.3rem]" : "px-6 h-[3rem]"
                } transition-all duration-500 ease-in-out`}
              >
                <Tooltip
                  delay={100}
                  content={
                    <p className="text-base p-1 font-medium text-neutral-700">
                      Add
                    </p>
                  }
                  placement="top"
                  size="sm"
                  radius="full"
                  shadow="sm"
                  showArrow={true}
                  closeDelay={0}
                  offset={25}
                >
                  {isAdding ? (
                    <button className="group relative p-2">
                      <i className="fa-solid fa-circle-check fa-fw text-2xl text-indigo-700 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></i>
                    </button>
                  ) : (
                    <button
                      className="group relative p-2"
                      onClick={handleAddToDay}
                    >
                      <i className="fa-light fa-circle-plus fa-fw text-2xl transition-opacity duration-300 ease-in-out group-hover:opacity-0 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-100"></i>
                      <i className="fa-solid fa-circle-plus fa-fw text-2xl text-indigo-700 transition-opacity duration-300 ease-in-out absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100"></i>
                    </button>
                  )}
                </Tooltip>
                <Tooltip
                  delay={100}
                  content={
                    <p className="text-base p-1 font-medium text-neutral-700">
                      Share
                    </p>
                  }
                  placement="top"
                  size="sm"
                  radius="full"
                  shadow="sm"
                  showArrow={true}
                  closeDelay={0}
                  offset={25}
                >
                  <button className="group relative p-2 ml-8">
                    <i className="fa-light fa-paper-plane text-2xl transition-opacity duration-300 ease-in-out group-hover:opacity-0 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-100"></i>
                    <i className="fa-solid fa-paper-plane text-2xl text-blue-700 transition-opacity duration-300 ease-in-out absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100"></i>
                  </button>
                </Tooltip>
                <Tooltip
                  delay={100}
                  content={
                    <p className="text-base p-1 font-medium text-neutral-700">
                      Like
                    </p>
                  }
                  placement="top"
                  size="sm"
                  radius="full"
                  shadow="sm"
                  showArrow={true}
                  closeDelay={0}
                  offset={25}
                >
                  <button
                    onClick={handleLike}
                    className="group relative p-2 ml-10"
                  >
                    {isLiked ? (
                      <i className="fa-solid fa-heart text-2xl text-red-600 transition-opacity duration-300 ease-in-out absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></i>
                    ) : (
                      <i className="fa-light fa-heart text-2xl transition-opacity duration-300 ease-in-out absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-100"></i>
                    )}
                    <i className="fa-solid fa-heart text-2xl text-red-600 transition-opacity duration-300 ease-in-out absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100"></i>
                  </button>
                </Tooltip>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const roundRatingToOneDecimal = (rating: number) => {
  return Math.round(rating * 100) / 100;
};

const formatTotalReviews = (totalReviews: number) => {
  if (totalReviews > 999) {
    return `${(totalReviews / 1000).toFixed(2)}k`;
  }
  return totalReviews.toString();
};

export default MoreInfoModalViator;
